import { Chip, LinearProgress, Tooltip } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import {
  useRecordContext,
  FieldProps,
  RaRecord,
  useTranslate,
  type Translate,
} from "react-admin";

const colorStatus = {
  done: "#0a5004",
  completed: "#0a5004",
  error: "#702020",
  failed: "#702020",
  cached: "#2F3C2F",
  running: "#151535",
  "in-progress": "#151535",
  scheduled: "#456565",
};

function AnalysisStatusField(props: FieldProps) {
  const record = useRecordContext();
  const [progress, setProgress] = useState(0);
  const t = useTranslate();

  const analysis = useMemo(
    () =>
      record?.analysis
        ? {
            ...record.analysis,
            status: record.analysis?.status,
            started_at: record.analysis?.started_at,
            estimated_duration: record.analysis?.estimated_duration,
          }
        : record,
    [record],
  );
  const { status } = analysis;

  useEffect(() => {
    let interval: NodeJS.Timer | undefined;
    const { status, started_at, estimated_duration } = analysis;
    if ((status === "running" || status === "in-progress") && started_at) {
      interval = setInterval(() => {
        const elapsedTime = Date.now() - new Date(started_at + "Z").getTime();
        if (isNaN(estimated_duration)) {
          const estimatedTime =
            new Date(estimated_duration + "Z").getTime() -
            new Date(started_at + "Z").getTime();
          const estimatedProgress =
            Math.floor((elapsedTime / estimatedTime) * 10000) / 100;
          setProgress(estimatedProgress);
        } else {
          const h =
            Math.floor(
              (elapsedTime / (elapsedTime + estimated_duration * 1000)) * 10000,
            ) / 100;
          setProgress(h);
        }
      }, 1000);
    } else {
      setProgress(0);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [analysis]);

  if (isNaN(progress) || progress === 0 || progress > 100) {
    const tooltip = getErrorText(analysis, t);
    return (
      <Tooltip title={tooltip} placement="top">
        <Chip
          style={{ backgroundColor: colorStatus[status] }}
          label={t("resources.status." + status)}
        />
      </Tooltip>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        paddingRight: "1em",
        minWidth: "10em",
        maxWidth: "15em",
        margin: "auto",
      }}
    >
      <LinearProgress
        color="primary"
        variant={progress != null ? "determinate" : "indeterminate"}
        style={{ height: 10, borderRadius: 4, marginTop: 6, width: "100%" }}
        value={progress}
      />
      <span style={{ marginLeft: "-60%", zIndex: "1" }}>{progress}%</span>
    </div>
  );
}

export default AnalysisStatusField;

function getErrorText(record: RaRecord, t: Translate) {
  if (!["failed", "error"].includes(record.status)) return;
  const errorMessage: string = record.steps
    ? record.steps
        .filter((step) => step.status === "error")
        .map((step) => step.name + ": " + step.errorMessage)
        .join("\n")
    : t("resource.error.message.unknown");
  const isErrorUnknown =
    record?.exitCode === 1 ||
    record?.errorMessage === "Unknonwn error" ||
    errorMessage.includes(t("resource.error.message.unknown")) ||
    errorMessage.includes("Unknonwn error"); // TODO Remove when typo in qyerror is removed
  if (isErrorUnknown) return t("resource.error.message.unknown");

  if (record?.isInputError) {
    return `${record?.errorMessage || errorMessage} : ${t(
      "resource.error.message.input",
    )}`;
  }

  return record?.errorMessage || errorMessage;
}
